import React, { useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentArrowUpIcon, DocumentTextIcon, PhotoIcon } from "@heroicons/react/24/outline";
import ShortEditableContent from "../KaTeXComponent/ShortEditableContent";
import { isBlankHtml } from "../KaTeXComponent/RichText";
import Select from "../forms/Select/Select";
import { focusRing, linkButtonClass, Spinner } from "../../pages/Exams/TestBuilder/ui";
import useBankLists from "./useBankLists";
import { canCreateIn, subjectFolderOptions } from "../../utils/folders";

export const emptyPassageForm = { name: "", subjectUuid: null, folderUuid: null, body: "", file: null };

// The text's tools (a picture, a .docx file): quiet, next to the field's label; 44px tall on phones.
const toolButton = `inline-flex h-11 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 md:h-8 ${focusRing}`;

/** The passage's name with its hint (only teachers see it); also the one field of a printed passage. */
export function PassageNameField({ value, onChange, error, disabled = false }) {
  const [t] = useTranslation();
  const nameId = useId();
  const nameHintId = useId();
  return (
    <div>
      <label htmlFor={nameId} className="mb-1 block text-sm font-medium text-slate-700">
        {t("passages.editor.name")}
      </label>
      <input
        id={nameId}
        type="text"
        value={value}
        maxLength={255}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("passages.editor.name_placeholder")}
        aria-invalid={!!error || undefined}
        aria-describedby={nameHintId}
        className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15 disabled:bg-slate-50"
      />
      <p id={nameHintId} className="mt-1 text-xs text-slate-500">
        {t("passages.editor.name_hint")}
      </p>
      {error && <p className="mt-1 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}

/**
 * The passage form: name, subject, folder and the text (typed, pasted from Word, or a .docx file whose
 * content replaces the text when the form is saved). Controlled: `value` = {name, subjectUuid, folderUuid,
 * body, file}, `onChange(patch)`; a typed body comes with `bodyEdited: true`. The text editor reads
 * `value.body` when it mounts: change `editorKey` to load another body into it. `onUploadingChange(true)`
 * while a picture uploads, so the dialog can hold back saving.
 * Folders offered: the subjects where the role creates (author, moderator, admin), plus `keepFolderUuid` (the
 * passage's current folder, so it shows); "No folder" only with `allowNoFolder` (the global rights).
 */
export default function PassageEditor({
  value,
  onChange,
  editorKey = 0,
  errors = {},
  disabled = false,
  onUploadingChange,
  keepFolderUuid = null,
  allowNoFolder = true,
}) {
  const [t] = useTranslation();
  const { subjects, folders } = useBankLists();
  const fileRef = useRef(null);
  const editorApi = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);

  const subjectOptions = useMemo(() => subjects.map((s) => ({ value: s.uuid, label: s.name })), [subjects]);
  // The passage takes the folder's subject.
  const folderOptions = useMemo(
    () => [
      ...(allowNoFolder ? [{ value: "", label: t("passages.editor.folder_placeholder") }] : []),
      ...subjectFolderOptions(folders, (f) => canCreateIn(f.role) || f.uuid === keepFolderUuid),
    ],
    [folders, allowNoFolder, keepFolderUuid, t]
  );
  const folderSubject = folders.find((f) => f.uuid === value.folderUuid)?.subject || null;
  const placeholder = t("passages.editor.body_placeholder");

  return (
    <div className="space-y-4">
      <PassageNameField value={value.name} onChange={(name) => onChange({ name })} error={errors.name} disabled={disabled} />

      <div className="grid gap-4 sm:grid-cols-2">
        {folders.length > 0 && (
          <Select
            label={t("folders.subject_folder")}
            options={folderOptions}
            value={value.folderUuid || ""}
            onChange={(o) => {
              const subject = folders.find((f) => f.uuid === o?.value)?.subject;
              onChange({ folderUuid: o?.value || null, ...(subject ? { subjectUuid: subject.uuid } : {}) });
            }}
            isSearchable
            isDisabled={disabled}
            placeholder={t("folders.subject_folder_placeholder")}
            errorMsg={errors.folder}
          />
        )}
        <Select
          label={t("passages.editor.subject")}
          placeholder={t("passages.editor.subject_placeholder")}
          options={subjectOptions}
          value={folderSubject?.uuid || value.subjectUuid || ""}
          onChange={(o) => onChange({ subjectUuid: o?.value || null })}
          isSearchable
          isDisabled={disabled || !!folderSubject}
          errorMsg={errors.subject}
        />
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-slate-700">{t("passages.editor.body")}</span>
          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onChange({ file });
            }}
          />
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => editorApi.current?.pickImage()}
              disabled={disabled || imageUploading}
              aria-busy={imageUploading || undefined}
              className={toolButton}
            >
              {imageUploading ? <Spinner /> : <PhotoIcon className="h-4 w-4 shrink-0" aria-hidden="true" />}
              {t("passages.editor.insert_image")}
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled} className={toolButton}>
              <DocumentArrowUpIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t("passages.editor.upload_docx")}
            </button>
          </div>
        </div>
        {value.file && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
            <span className="min-w-0 flex-1 break-words">{t("passages.editor.file_chosen", { name: value.file.name })}</span>
            <button type="button" onClick={() => onChange({ file: null })} disabled={disabled} className={linkButtonClass}>
              {t("passages.editor.remove_file")}
            </button>
          </div>
        )}
        {/* The hint is drawn over the editor, not put into it: a placeholder inside the content would be
            saved with the text (e.g. when a picture is inserted into an empty editor). */}
        <div className="relative">
          <ShortEditableContent
            ref={editorApi}
            key={editorKey}
            value={value.body}
            onChange={(html) => onChange({ body: html, bodyEdited: true })}
            onUploadingChange={(uploading) => {
              setImageUploading(uploading);
              onUploadingChange?.(uploading);
            }}
            ariaLabel={t("passages.editor.body")}
            sizeClassName="passage-content min-h-[14rem] max-h-[26rem] overflow-y-auto leading-relaxed"
            richImages
            hideImageButton
          />
          {isBlankHtml(value.body) && (
            <span aria-hidden="true" className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400">
              {placeholder}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">{t("passages.editor.image_hint")}</p>
        <p className="mt-0.5 text-xs text-slate-500">{t("passages.editor.upload_hint")}</p>
        {errors.body && <p className="mt-1 text-xs font-medium text-red-600">{errors.body}</p>}
      </div>
    </div>
  );
}
